import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';
import { teamAPI } from '../lib/api';
import { 
  GraduationCap, 
  ArrowLeft,
  Target,
  Heart,
  Award,
  Users,
  BookOpen,
  Sparkles,
  ArrowRight,
  User
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to resolve team profile image URLs (handles relative /api/... paths)
const getTeamImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  if (imageUrl.startsWith('http')) return imageUrl;
  if (imageUrl.startsWith('/')) return `${API_URL}${imageUrl}`;
  return imageUrl;
};

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

const AboutPage = () => {
  const [team, setTeam] = useState([]);
  const [teamLoading, setTeamLoading] = useState(true);

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const response = await teamAPI.list();
        setTeam(response.data.team_members || []);
      } catch (error) {
        console.error('Error fetching team members:', error);
        // Fallback to default if API fails
        setTeam([{
          member_id: 'default',
          full_name: "Ifeanyichukwu Marcel Onovo",
          title: "Founder & CEO",
          bio: "Ontario Educator and Mathematics Specialist with a strong background in academic instruction and student support. Founder of Gifted Academy Tutors, committed to helping students build confidence, master complex concepts, and achieve lasting academic success through personalized, results-driven learning.",
          profile_image: "https://customer-assets.emergentagent.com/job_academic-match-3/artifacts/4o5ufoqt_1000210618.jpg"
        }]);
      } finally {
        setTeamLoading(false);
      }
    };
    fetchTeam();
  }, []);

  const values = [
    {
      icon: Target,
      title: "Excellence",
      description: "We are committed to providing the highest quality tutoring services to help students achieve their academic goals."
    },
    {
      icon: Heart,
      title: "Passion for Education",
      description: "Our tutors are passionate educators who genuinely care about student success and growth."
    },
    {
      icon: Users,
      title: "Personalized Learning",
      description: "Every student is unique. We tailor our approach to match individual learning styles and needs."
    },
    {
      icon: Award,
      title: "Qualified Experts",
      description: "All our tutors undergo rigorous vetting to ensure they have the expertise and teaching skills needed."
    }
  ];

  const stats = [
    { number: "500+", label: "Students Helped" },
    { number: "50+", label: "Expert Tutors" },
    { number: "10,000+", label: "Sessions Completed" },
    { number: "4.9/5", label: "Average Rating" }
  ];

  return (
    <div className="min-h-screen bg-stone-50" data-testid="about-page">
      <SEO 
        title="About Us - Expert Tutoring in Math, Physics & Chemistry"
        description="Learn about Gifted Academy Tutors - our mission to provide high-quality personalized tutoring in Mathematics, Physics, and Chemistry for high school and university students."
        keywords="about gifted academy tutors, tutoring company, math physics chemistry tutoring, education mission"
      />
      {/* Header */}
      <header className="bg-white border-b border-stone-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="h-5 w-5" />
                </Button>
              </Link>
              <Link to="/" className="flex items-center gap-2">
                <GraduationCap className="h-8 w-8 text-indigo-700" />
                <span className="font-heading text-xl sm:text-2xl font-semibold text-slate-900">
                  Gifted Academy Tutors
                </span>
              </Link>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="rounded-full">Sign In</Button>
              </Link>
              <Link to="/register">
                <Button className="rounded-full bg-slate-900 hover:bg-slate-800">Get Started</Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-gradient-to-b from-white to-stone-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeUp}>
              <p className="text-sm font-medium tracking-wide uppercase text-indigo-700 mb-4">
                About Us
              </p>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-medium text-slate-900 mb-6 leading-tight">
                Empowering Students to <span className="text-indigo-700">Excel</span>
              </h1>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Gifted Academy Tutors was founded with a simple mission: to make high-quality, personalized tutoring accessible to every student. We connect learners with expert tutors who are passionate about education and dedicated to helping students reach their full potential.
              </p>
              <Link to="/tutors">
                <Button className="rounded-full bg-slate-900 hover:bg-slate-800 px-8 py-6 text-lg">
                  Meet Our Tutors <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1760131556605-7f2e63d00385?crop=entropy&cs=srgb&fm=jpg&q=85&w=700"
                alt="Modern education"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-4 shadow-float">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                    <Sparkles className="h-6 w-6 text-indigo-700" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Since 2020</p>
                    <p className="text-sm text-slate-500">Transforming education</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 px-6 md:px-12 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                {...fadeUp}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <p className="font-heading text-4xl md:text-5xl font-bold text-white mb-2">
                  {stat.number}
                </p>
                <p className="text-slate-400">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            <motion.div {...fadeUp}>
              <Card className="border-stone-100 h-full">
                <CardContent className="p-8">
                  <div className="w-14 h-14 rounded-xl bg-indigo-100 flex items-center justify-center mb-6">
                    <Target className="h-7 w-7 text-indigo-700" />
                  </div>
                  <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                    Our Mission
                  </h2>
                  <p className="text-slate-600 leading-relaxed">
                    To provide accessible, high-quality tutoring that empowers students to overcome academic challenges, build confidence, and achieve excellence in their studies. We believe every student has the potential to succeed with the right guidance and support.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
            <motion.div {...fadeUp} transition={{ delay: 0.1 }}>
              <Card className="border-stone-100 h-full">
                <CardContent className="p-8">
                  <div className="w-14 h-14 rounded-xl bg-amber-100 flex items-center justify-center mb-6">
                    <BookOpen className="h-7 w-7 text-amber-700" />
                  </div>
                  <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                    Our Vision
                  </h2>
                  <p className="text-slate-600 leading-relaxed">
                    To become the world's most trusted online tutoring platform, known for transforming students' academic journeys through personalized, expert-led education. We envision a future where geographic and economic barriers no longer limit access to quality education.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-stone-50">
        <div className="max-w-7xl mx-auto">
          <motion.div className="text-center mb-16" {...fadeUp}>
            <p className="text-sm font-medium tracking-wide uppercase text-indigo-700 mb-4">
              What We Stand For
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-medium text-slate-900 mb-4">
              Our Core Values
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              These principles guide everything we do at Gifted Academy
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={index}
                {...fadeUp}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="border-stone-100 h-full hover:shadow-float transition-shadow">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center mb-4">
                      <value.icon className="h-6 w-6 text-indigo-700" />
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
                      {value.title}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div className="text-center mb-16" {...fadeUp}>
            <p className="text-sm font-medium tracking-wide uppercase text-indigo-700 mb-4">
              Leadership
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-medium text-slate-900 mb-4">
              Meet Our Team
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              The dedicated people behind Gifted Academy
            </p>
          </motion.div>

          {teamLoading ? (
            <div className="flex justify-center">
              <div className="animate-pulse bg-stone-200 rounded-xl w-full max-w-md h-64"></div>
            </div>
          ) : team.length === 1 ? (
            <div className="flex justify-center">
              {team.map((member, index) => (
                <motion.div
                  key={member.member_id || index}
                  {...fadeUp}
                  transition={{ delay: index * 0.1 }}
                  className="max-w-md w-full"
                >
                  <Card className="border-stone-100 overflow-hidden">
                    <CardContent className="p-8 text-center">
                      {member.profile_image ? (
                        <img 
                          src={getTeamImageUrl(member.profile_image)} 
                          alt={member.full_name}
                          className="w-32 h-32 rounded-full mx-auto mb-6 object-cover object-top border-4 border-indigo-100"
                        />
                      ) : (
                        <div className="w-32 h-32 rounded-full mx-auto mb-6 bg-gradient-to-br from-indigo-400 to-indigo-600 flex items-center justify-center border-4 border-indigo-100">
                          <User className="w-16 h-16 text-white" />
                        </div>
                      )}
                      <h3 className="font-heading text-xl font-semibold text-slate-900 mb-1">
                        {member.full_name}
                      </h3>
                      <p className="text-indigo-700 font-medium text-sm mb-4">
                        {member.title}
                      </p>
                      <p className="text-slate-600 text-sm leading-relaxed">
                        {member.bio}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {team.map((member, index) => (
                <motion.div
                  key={member.member_id || index}
                  {...fadeUp}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="border-stone-100 overflow-hidden h-full">
                    <CardContent className="p-6 text-center">
                      {member.profile_image ? (
                        <img 
                          src={getTeamImageUrl(member.profile_image)} 
                          alt={member.full_name}
                          className="w-24 h-24 rounded-full mx-auto mb-4 object-cover object-top border-4 border-indigo-100"
                        />
                      ) : (
                        <div className="w-24 h-24 rounded-full mx-auto mb-4 bg-gradient-to-br from-indigo-400 to-indigo-600 flex items-center justify-center border-4 border-indigo-100">
                          <User className="w-12 h-12 text-white" />
                        </div>
                      )}
                      <h3 className="font-heading text-xl font-semibold text-slate-900 mb-1">
                        {member.full_name}
                      </h3>
                      <p className="text-indigo-700 font-medium text-sm mb-3">
                        {member.title}
                      </p>
                      <p className="text-slate-600 text-sm">
                        {member.bio}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-stone-50">
        <div className="max-w-4xl mx-auto">
          <motion.div className="text-center mb-12" {...fadeUp}>
            <p className="text-sm font-medium tracking-wide uppercase text-indigo-700 mb-4">
              Our Journey
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-medium text-slate-900 mb-4">
              Our Story
            </h2>
          </motion.div>

          <motion.div 
            className="prose prose-lg prose-slate max-w-none"
            {...fadeUp}
            transition={{ delay: 0.1 }}
          >
            <div className="space-y-6 text-slate-600 leading-relaxed">
              <p>
                Gifted Academy Tutors was born out of a deep passion for education and a personal understanding of the challenges many students face in their academic journey.
              </p>
              
              <p>
                As an educator with a strong background in Mathematics and years of experience supporting students one-on-one, I have worked closely with learners who struggled not because they lacked ability, but because they lacked the right support. Many of them had the potential to succeed, yet felt overwhelmed, discouraged, or left behind in traditional classroom settings.
              </p>
              
              <p>
                Through my experience as a tutor, mentor, and educational assistant, I began to see a pattern—students thrive when they are given personalized attention, patience, and the opportunity to truly understand concepts at their own pace.
              </p>
              
              <p className="text-indigo-700 font-semibold text-xl">
                Gifted Academy Tutors was created to bridge that gap.
              </p>
              
              <p>
                What started as a simple effort to help a few students grew into a mission: to build a platform where every student feels supported, understood, and empowered to succeed. Over time, I witnessed remarkable transformations—students who once struggled began gaining confidence, improving their grades, and even pursuing advanced studies in subjects they once feared.
              </p>
              
              <p>
                Today, Gifted Academy Tutors continues to grow with that same vision—to provide high-quality, personalized learning experiences that go beyond just improving grades. Our goal is to build confidence, develop critical thinking, and help students unlock their full academic potential.
              </p>
              
              <p className="text-slate-900 font-medium text-lg">
                This is more than tutoring. It is a commitment to student success.
              </p>
              
              <p className="text-indigo-700 italic text-lg border-l-4 border-indigo-200 pl-6 py-2">
                Every student has potential. Sometimes, they just need the right guidance to discover it.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 md:px-12 bg-slate-900">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div {...fadeUp}>
            <h2 className="font-heading text-3xl md:text-4xl font-medium text-white mb-6">
              Ready to Start Learning?
            </h2>
            <p className="text-lg text-slate-300 mb-8">
              Join thousands of students achieving academic excellence with Gifted Academy Tutors.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/tutors">
                <Button className="rounded-full bg-white text-slate-900 hover:bg-stone-100 px-8 py-6 text-lg">
                  Find a Tutor
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="rounded-full border-slate-600 text-white hover:bg-slate-800 px-8 py-6 text-lg">
                  Contact Us
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 md:px-12 bg-stone-50 border-t border-stone-100">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-slate-500 text-sm">
            © 2024 Gifted Academy Tutors. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
